import { Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface RemoveSuggestionsModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  /** For the body copy ("…no longer be shown for {{name}}"). */
  residentFirstName?: string
  /** Disables Remove while the rejections are in flight. */
  isRemoving?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * "Don't show this again" confirmation (per the Figma prototype). Confirming
 * permanently rejects the resident's pending suggestions — immediately, not
 * on review save, so the choice survives reloads and applies to everyone.
 */
const RemoveSuggestionsModal: React.FC<RemoveSuggestionsModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  residentFirstName,
  isRemoving = false,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)

  return (
    <StyledModal
      localisation={{
        title: t(carePlansConstants.REMOVE_SUGGESTIONS_MODAL_TITLE),
        confirmText: t(carePlansConstants.REMOVE_SUGGESTIONS_MODAL_CONFIRM),
        cancelText: t(carePlansConstants.REMOVE_SUGGESTIONS_MODAL_CANCEL)
      }}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ variant: 'danger', disabled: isRemoving }}
      data-testid="remove-suggestions-modal"
    >
      <Divider style={{ margin: '0' }} />
      <StyledContent>
        <Text level={1}>
          {t(carePlansConstants.REMOVE_SUGGESTIONS_MODAL_BODY, {
            name: residentFirstName ?? ''
          })}
        </Text>
        <Text level={1} weight="medium">
          {t(carePlansConstants.REMOVE_SUGGESTIONS_MODAL_QUESTION)}
        </Text>
      </StyledContent>
    </StyledModal>
  )
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  /* Design order: Remove on the left, Cancel outermost right. */
  .ant-modal-footer {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-start;
    gap: 16px;
    margin-top: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;

    .ant-btn + .ant-btn {
      margin-inline-start: 0 !important;
    }
  }
`

export default RemoveSuggestionsModal
